import { useEffect, useState } from 'react';

import { useDoubleTheDonation } from '+/contexts/DoubleTheDonationContext';

const isHTMLInputElement = (element: any): element is HTMLInputElement => {
  return 'value' in element;
};

const getElementByClass = (elementKey: string) => {
  const list = document.getElementsByClassName(elementKey);
  return list.length > 0 ? list[0] : null;
};

const getElementByName = (elementKey: string) => {
  const list = document.getElementsByName(elementKey);
  return list.length > 0 ? list[0] : null;
};

export const useDoubleTheDonationSetup = () => {
  const [doubleTheDonationExists, setDoubleTheDonationExists] = useState(false);
  const { state: doubleTheDonationState, dispatch: doubleTheDonationDispatch } =
    useDoubleTheDonation();

  const setupDoubleTheDonation = () => {
    const API_KEY = process.env.GATSBY_DOUBLE_THE_DONATION_API_KEY;
    if (!document.getElementById('dd-config-script')) {
      const configScript = document.createElement('script');
      configScript.id = 'dd-config-script';
      configScript.textContent = `var DDCONF = {API_KEY: '${API_KEY}'};`;
      document.head.appendChild(configScript);
    }

    if (!document.getElementById('dd-plugin-script')) {
      const pluginScript = document.createElement('script');
      pluginScript.id = 'dd-plugin-script';
      pluginScript.src = 'https://doublethedonation.com/api/js/ddplugin.js';
      pluginScript.async = true;
      document.head.appendChild(pluginScript);

      pluginScript.onload = () => {
        window.doublethedonation?.plugin?.load_streamlined_input?.();
      };
    }

    return () => {
      const configScript = document.getElementById('dd-config-script');
      const pluginScript = document.getElementById('dd-plugin-script');

      if (configScript) document.head.removeChild(configScript);
      if (pluginScript) document.head.removeChild(pluginScript);
    };
  };

  const getDoesDoubleTheDonationExist = (elementKey: string) => {
    let attempts = 0;
    const maxAttempts = 10;
    const intervalTime = 500;

    const checkForElement = () => {
      const element = getElementByName(elementKey);
      if (element && isHTMLInputElement(element)) {
        setDoubleTheDonationExists(true);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkForElement, intervalTime);
      } else {
        setDoubleTheDonationExists(false);
      }
    };

    checkForElement();
  };

  const handleSelection = () => {
    let lastName = '';
    let lastId = '';

    const observer = new MutationObserver(() => {
      const selectedCompanyElement = getElementByClass('dtd-company-selected');
      if (selectedCompanyElement) {
        const nameElement = getElementByName('doublethedonation_company_name');
        const idElement = getElementByName('doublethedonation_company_id');

        if (isHTMLInputElement(nameElement) && isHTMLInputElement(idElement)) {
          const name = nameElement.value;
          const id = idElement.value;

          if (name !== lastName || id !== lastId) {
            lastName = name;
            lastId = id;

            doubleTheDonationDispatch({
              type: 'updateDoubleTheDonation',
              payload: {
                privateDoubleTheDonationCompanyName: name,
                doubleTheDonationCompanyId: id,
              },
            });
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  };

  useEffect(() => {
    setupDoubleTheDonation();
    getDoesDoubleTheDonationExist('doublethedonation_company_name_input');
    handleSelection();
  }, []);

  return {
    doubleTheDonationExists,
    doubleTheDonationState,
    doubleTheDonationDispatch,
  };
};

export const useCheckForEmailInput = (controller: EveryActionModelController) => {
  const checkForEmailInput = () => {
    if (window.doublethedonation) {
      const domain = window.doublethedonation.integrations.core.strip_domain(
        controller.getEmailAddress() || '',
      );

      if (domain) {
        window.doublethedonation.plugin.email_domain(domain);
      }
    }
  };

  useEffect(() => {
    checkForEmailInput();
  }, [controller.getEmailAddress()]);
};
